/**
 * Free-form chemistry lab, gated by `chemistry_lab`. Client-held bench state
 * (see chemistryLab/labState.ts for why); every mix action calls POST
 * /chemistry-lab/action and renders the outcome. See
 * docs/FeatureDesigns/chemistry-lab-target.md.
 */
import { CloseButton, ThemeIcon } from '../components/PaneButtons';
import '../components/BenchType.css';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { GuidedProcedure, Stoichiometry, VesselContentInput } from '@padcanvas/shared';
import type { GasId, LabMode, TemperatureBand, ChemistryLabActionResponse, ChemistryLabSession } from './types';
import { ROOM_C, BOILING_C, bandFor, isBoilingNow, temperatureStepLabel } from './temperature';
import { initialContainers, newContainer, newStepId, addToContents, substanceIdsOf, hasRoomFor, type LabContainer, type LabStep } from './labState';
import { brimVolumeML } from './vesselGeometry';
import { VESSEL_TYPES, VESSEL_LABELS, type VesselType } from './vesselTypes';
import {
  defaultStandFor, isStandValidFor, standAllowsBurner, standForBurner, standsFor,
  STAND_CAPACITY, STAND_HOME_VESSEL, STAND_LABELS, type StandType,
} from './standTypes';
import type { BurnerType } from './burnerTypes';
import { benchLayout } from './benchLayout';
import { findSubstance, intrinsicOutcomeFor, formatAmount, mlOf, perAddFor, GAS_SUBSTANCE_IDS, type Substance } from './substanceCatalog';
import { useBoilingSound } from './useBoilingSound';
import { useBenchFullscreen } from '../components/useBenchFullscreen';
import { useBenchTouch } from '../components/useBenchTouch';
import { SubstanceTray, SubstancePicker } from './components/SubstanceTray';
import { ControlsPanel } from './components/ControlsPanel';
import { StepLog } from './components/StepLog';
import { ChemicalsIcon, ApparatusIcon, StepsIcon } from './components/PaneIcons';
import { GuidedPanel } from './components/GuidedPanel';
import { judge, reachedByHeat, stepContainer, type BenchAttempt } from './guidedRun';
import { ContainerContextMenu } from './components/ContainerContextMenu';
import { StandContextMenu } from './components/StandContextMenu';
import { ChemistryLabScene2D, DEFAULT_POUR_MS, dropSequenceMs, pourStages } from './components/ChemistryLabScene2D';
import { FullscreenIcon } from '../components/FullscreenIcon';
import { BenchPhoneHeader, type BenchPhoneHeaderProps } from '../components/BenchPhoneHeader';
import { BenchMark } from './components/BenchMark';
import { ReagentSearch } from './components/ReagentSearch';
import { StrengthChooser } from './components/StrengthChooser';
import { countedMass, formatMolarity } from './quantityFormat';
import { useLabSetups, SaveSetupDialog, type LabSaving } from '../lab-setups';
import {
  benchSetup, containersFromSetup, procedureOf, recordAttempt, recordHeat, sameSetup, toRecorded, vesselNames,
  CHEMISTRY_CHAPTER_OPTIONS, CHEMISTRY_SETUP_VERSION,
  type ChemistrySetupPayload, type ChemistrySetupVessel, type RecordedStep,
} from './setupState';
import { StepsEditor } from './components/StepsEditor';
import './ChemistryBench.css';

// Eight, not four: a full four-tube rack is one piece of apparatus, and a
// bench that could hold nothing else alongside it would be a strange lab.
// Matches the scene's own capacity — two shelves of MAX_PER_SHELF (4) each,
// the most the bench zooms/shelves down to before it would need to scroll.
const MAX_CONTAINERS = 8;

/** How long the burner's slider must sit still before its outcome is asked
 *  for. A drag crosses band boundaries several times on the way to where the
 *  student is actually going; without this, each crossing is a request for an
 *  answer they are already scrolling past. */
const TEMPERATURE_SETTLE_MS = 1000;

/**
 * How the bench talks to a backend. Each site supplies its own: the base URL
 * and where an auth token is kept differ, and ScienceXP mints an anonymous
 * guest on the way in where BuzzBrain already has a signed-in student.
 */
export interface ChemistryBenchApi {
  /** `mode` is what the door chose. Absent means the free bench, which is what
   *  every caller wanted before there was a second one. */
  startSession: (mode?: LabMode) => Promise<ChemistryLabSession>;
  action: (
    /** What is in the vessel, with how much of each — the amounts the bench
     *  already tracks. They reach the chemistry in neither mode: the mix key
     *  and the prompt see ids alone. On a quantitative bench they are what the
     *  working is computed from. */
    contents: VesselContentInput[],
    temperatureBand: TemperatureBand,
    action: 'add-substance' | 'apply-heat' | 'apply-test' | 'combine-containers',
    sessionId: string,
    /** 'vapor' for a rod held near another container's escaping gas — these
     *  substances are meeting as fumes in open air, not a shared liquid.
     *  Absent for an ordinary mix (every other call site). */
    mixPhase?: 'liquid' | 'vapor',
  ) => Promise<ChemistryLabActionResponse>;
}

export interface ChemistryBenchProps {
  api: ChemistryBenchApi;
  /** Run a written procedure instead of the free-form bench: the vessels are
   *  the experiment's, the right-hand panel is its script, and every action is
   *  checked against the step the student is on. Absent = free-form, which is
   *  the default and what the bench is without any of this.
   *  See docs/FeatureDesigns/chemistry-lab-guided-target.md. */
  experiment?: GuidedProcedure;
  /** Rendered in the bench's top bar — each site's own chrome (a back button
   *  and a title here, something else there). Absent, the bench names itself
   *  ("Chemistry Lab"), so a host that gives it nothing — a tab on a page
   *  whose tab row full screen hides — still has a bench that says what it is. */
  topbar?: React.ReactNode;
  /** The bar a phone held upright shows instead of `topbar` — the shared
   *  BenchPhoneHeader, so it matches the optics and mechanics benches. The
   *  title defaults to the bench's own name; back is left out when absent. */
  phoneHeader?: Pick<BenchPhoneHeaderProps, 'title' | 'back' | 'trailing'>;
  /**
   * How long a pour takes end to end, in milliseconds — the stream falling,
   * the level rising and the last of it settling, all inside this. 0 fills the
   * vessel instantly, with no stream and no rising level, which is also what
   * happens for anyone whose system asks for reduced motion however this is
   * set.
   *
   * A parameter because the answer is a judgement about the lesson rather than
   * about liquid: watching a beaker fill is worth three seconds the first few
   * times and is in the way by the twentieth reagent.
   */
  pourMs?: number;
  /**
   * Whether this is the QUANTITATIVE bench.
   *
   * Decided on the way in and fixed for the session, because a bench whose
   * earlier steps were answered without amounts and whose later ones were not
   * has a step log that cannot be read as a record of anything. It is one
   * switch, not two: in this mode the bench states how much went in AND the
   * chemistry is asked in those terms; in the other it does neither. A bench
   * that showed amounts it did not use was the confusing middle, and it is
   * gone.
   *
   * What does NOT change with it is the bench's behaviour: two drops are two
   * drops either way, the level rises with what is poured either way. The mode
   * decides whether any of that is SAID, and whether it reaches the chemistry.
   */
  quantitative?: boolean;
  /**
   * Saving to a teacher's setups — Save, the setups picker, and recording a
   * guided experiment of their own. Absent everywhere but the teacher app, and
   * then the bench is exactly what it was. See
   * docs/FeatureDesigns/lab-setups/lab-setups-target.md §6.4.
   */
  saving?: LabSaving<ChemistrySetupPayload>;
}

/**
 * The sizes the lab can be drawn at, as a multiple of the size it was designed
 * at. Steps rather than a slider: the difference between 1.15 and 1.2 is not a
 * decision anyone wants to make, and two buttons that always do something
 * beat a control that needs aiming.
 */
const UI_SCALE_STEPS = [1, 1.15, 1.3, 1.5, 1.75, 2];

function nearestStep(value: number): number {
  return UI_SCALE_STEPS.reduce((best, step) => (
    Math.abs(step - value) < Math.abs(best - value) ? step : best
  ), UI_SCALE_STEPS[0]);
}

/**
 * Where to start, from the width of the window.
 *
 * A 13-to-15-inch laptop reports roughly 1280-1710 CSS pixels; a 27-inch
 * monitor at native resolution reports 2560, and one at 2x reports 1280-1920.
 * So width is a decent proxy for "there is more room here than the type was
 * drawn for" and a hopeless one for viewing distance — which is why this only
 * picks the first setting and never overrides a chosen one.
 */
function scaleForWidth(width: number): number {
  if (width >= 2100) return 1.3;
  if (width >= 1600) return 1.15;
  return 1;
}

/** Whether the reader has asked their system for less movement. Read at the
 *  moment of the pour, not once at mount: it is a setting they can change
 *  while the bench is open. */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The bench an experiment starts on — its own glassware, or the free-form
 *  bench's single default vessel. Setting out apparatus is not what these
 *  activities teach; see the guided target doc. */
function startingContainers(experiment?: GuidedProcedure, quantitative = false): LabContainer[] {
  // An experiment declares its own glassware, and that declaration wins: a
  // procedure written for a test tube is written for a test tube.
  if (!experiment) return initialContainers(quantitative);
  return experiment.bench.map(v => newContainer(v.vesselType));
}



export default function ChemistryBench({
  api, topbar, phoneHeader, experiment: experimentProp, pourMs = DEFAULT_POUR_MS, quantitative: quantitativeDoor = false,
  saving,
}: ChemistryBenchProps): React.JSX.Element {
  // ── Saved setups (teacher app) ────────────────────────────────────────────
  // The bench with amounts keeps its own list: a session's mode is fixed, so a
  // setup opens on the bench it was saved from.
  const setups = useLabSetups<ChemistrySetupPayload>('chemistry-lab', CHEMISTRY_SETUP_VERSION, saving, quantitativeDoor ? 'quantitative' : undefined);
  const opened = saving?.opened?.payload;
  /** The bench a saved setup sets out — what Start over and Record again go back to. */
  const [start, setStart] = useState<ChemistrySetupVessel[] | null>(() => opened?.vessels ?? null);
  /** A teacher's own guided experiment, being played. */
  const [script, setScript] = useState<ChemistrySetupPayload | null>(() => (opened?.steps?.length ? opened : null));
  const scriptTitle = setups.current?.name ?? 'Guided experiment';
  const scriptProcedure = useMemo(() => (script ? procedureOf(script, scriptTitle) : undefined), [script, scriptTitle]);
  const experiment = experimentProp ?? scriptProcedure;
  /** What is being recorded: the bench it started from, and the steps so far.
   *  A ref beside the state, because the burner's settle timer reads it long
   *  after the render that set it. */
  const [recording, setRecordingState] = useState<{ start: ChemistrySetupVessel[]; steps: RecordedStep[] } | null>(null);
  const recordingRef = useRef(recording);
  const setRecording = (next: typeof recording): void => { recordingRef.current = next; setRecordingState(next); };
  /** Why the last thing tried while recording was not recorded. */
  const [recordingWhy, setRecordingWhy] = useState<string | undefined>();
  /** The step editor, open on a fresh recording or on the saved steps. */
  const [editor, setEditor] = useState<{ vessels: ChemistrySetupVessel[]; steps: RecordedStep[]; conclusion?: string; from: 'recording' | 'saved' } | null>(null);
  const [saveDialog, setSaveDialog] = useState<'new' | 'copy' | null>(null);
  /** Bumped when a saved bench is set out, so its mixtures are worked out again. */
  const [openToken, setOpenToken] = useState(() => (opened ? 1 : 0));

  /**
   * Which bench this is.
   *
   * An ACTIVITY that declares `mode: 'quantitative'` is one, whichever door it
   * was opened through — its steps ask for numbers, and numbers are computed
   * only on this bench. Deciding that here rather than in each host is the
   * difference between one rule and three places to forget it: both sites open
   * an experiment by id alone, and neither knew to look at its mode.
   */
  const quantitative = quantitativeDoor || experiment?.mode === 'quantitative';
  const [containers, setContainers] = useState<LabContainer[]>(() => (start ? containersFromSetup(start) : startingContainers(experiment, quantitative)));
  const [selectedIndex, setSelectedIndex] = useState(0);
  /**
   * Which panel is showing, on a phone.
   *
   * Desktop shows all three at once and ignores this entirely — the attribute
   * lands on the page either way and only the narrow media query reads it, so
   * there is one layout in the markup and one in the head, not two components.
   *
   * ONE OF THE THREE IS ALWAYS SHOWING. A phone with every pane closed gave
   * the bench the whole screen, and the bench does not want it: the glassware
   * is scaled to the WIDTH, so the extra height became a screen of empty wall
   * above a flask sitting on the floor of the frame. The pane is what that
   * height is for. The notebook is the resting one, in both orientations —
   * it is the pane that reports rather than asks, so it is the one you can
   * leave open while doing something else.
   *
   * But it is not the one a free bench OPENS on. A fresh bench has nothing to
   * report, and its notebook said only "add a substance to begin" with the
   * place to do that one tap away behind a button. A guided run opens on its
   * steps, which are the instructions.
   */
  const [sheet, setSheet] = useState<'chem' | 'apparatus' | 'notes'>(experiment ? 'notes' : 'chem');
  /**
   * One pane at a time, and the notebook is what "no pane" means.
   *
   * Opening chemicals closes the notebook because there is only ever one
   * pane; closing chemicals puts the notebook back rather than leaving the
   * space empty. That space is not the bench's to reclaim in either
   * orientation — sideways it is a column beside the bench, in portrait it is
   * the height the bench would otherwise fill with sky.
   */
  /** Pressing the pane that is already open puts the notebook back — and
   *  pressing the notebook's own button while it is open does nothing, since
   *  there is nothing behind it to go back to. */
  const toggleSheet = (which: 'chem' | 'apparatus' | 'notes') =>
    setSheet(prev => (prev !== which ? which : 'notes'));

  /** Dismiss the pane that is showing: back to the notebook. */
  const closeSheet = () => setSheet('notes');

  /**
   * A mix's result lives in the notebook, and on a phone the notebook is a
   * tab — behind the chemicals the student just poured from, so a reaction
   * finished with nothing on screen saying what it was. So the moment a mix
   * is sent off to be worked out, the Steps tab opens: its "reacting…" line
   * says something is happening, and the result lands where it is already
   * being looked at. A phone held upright only; wider, the notebook is always
   * in view.
   */
  const showStepsOnPhone = (): void => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 640px)').matches) return;
    setSheet('notes');
  };

  /* Turned sideways, or asked by the button below, the bench takes the screen
     itself — the layout hides our chrome, and this is the only way to hide the
     browser's. See useBenchFullscreen for what each platform allows. */
  const pageRef = useRef<HTMLDivElement>(null);

  /**
   * On a phone held upright the bench is 40% of the screen and the pane below
   * it — whichever is open — takes everything else. Measured rather than
   * written as CSS, because what sits above the bench is the host's: the
   * science site's masthead and the student app's are different heights, and
   * a pane sized by a guess at them left the bench anywhere from 40% to 52%.
   * Sets --chemlab-sheet-h, which the stylesheet sizes every pane and the
   * room kept under the bench by.
   */
  const BENCH_SHARE = 0.40;
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page || typeof window.matchMedia !== 'function') return;
    const phone = window.matchMedia('(max-width: 640px)');
    const size = (): void => {
      if (!phone.matches) { page.style.removeProperty('--chemlab-sheet-h'); return; }
      const header = page.querySelector('.bench-phone-header')?.getBoundingClientRect().height ?? 0;
      const tabs = page.querySelector('.chemlab-actionbar')?.getBoundingClientRect().height ?? 0;
      const sheet = page.clientHeight - header - tabs - BENCH_SHARE * window.innerHeight;
      page.style.setProperty('--chemlab-sheet-h', `${Math.max(160, Math.round(sheet))}px`);
    };
    size();
    const observer = new ResizeObserver(size);
    observer.observe(page);
    phone.addEventListener('change', size);
    window.addEventListener('resize', size);
    return () => {
      observer.disconnect();
      phone.removeEventListener('change', size);
      window.removeEventListener('resize', size);
    };
  }, []);
  const fullscreen = useBenchFullscreen(pageRef);
  useBenchTouch();

  /**
   * What has just gone into a vessel, so the bench can show it going in.
   *
   * View state, like the blocks': the chemistry is the same whether or not
   * anyone watched it pour. The token is what restarts the animation when the
   * same reagent is added twice in a row — the CSS runs on mount, and without
   * a changing key React would reuse the element and nothing would move.
   *
   * `intoEmpty` is the difference between a level that RISES and a surface
   * that is merely disturbed. The bench has one resting level per vessel, so
   * a second liquid does not raise it — showing it rise from the floor again
   * would mean emptying the vessel first, on screen, which is not what
   * happened.
   */
  const [pour, setPour] = useState<
    { index: number; substanceId: string; token: number; intoEmpty: boolean; ms: number } | null
  >(null);
  const pourTimer = useRef<number | null>(null);

  /**
   * How long THIS pour takes — which is a question about how much is going in.
   *
   * `pourMs` is the time a HALF-FULL vessel takes; everything else is that in
   * proportion, so 2 mL of concentrated acid arrives in a moment and filling a
   * test tube takes its time. A fixed length made those two identical, which
   * is the one thing pouring is not.
   *
   * Two exceptions, both because what is happening is not a volume arriving:
   * a run of DROPS takes as long as it takes to let that many drops go, and a
   * SOLID falls at the speed a solid falls.
   */
  const pourDurationFor = (substance: Substance, vesselType: VesselType): number => {
    if (pourMs <= 0) return 0;
    const perAdd = perAddFor(substance, vesselType);
    if (substance.dose?.unit === 'drop') return dropSequenceMs(substance, perAdd);
    const ml = mlOf(substance, perAdd);
    if (ml === null) return pourMs;
    const half = brimVolumeML(vesselType) / 2;
    // Floored and capped: below the floor the stream never reads as a stream,
    // and a vessel filled to the brim in one go should not hold the bench up.
    return Math.min(2.4 * pourMs, Math.max(600, (ml / half) * pourMs));
  };

  const showPour = (index: number, substanceId: string, intoEmpty: boolean, ms: number): void => {
    // Instant by request, and instant for anyone who has asked their system
    // for less movement: either way the vessel is simply filled.
    if (ms <= 0 || prefersReducedMotion()) return;
    if (pourTimer.current !== null) window.clearTimeout(pourTimer.current);
    setPour({ index, substanceId, token: Date.now(), intoEmpty, ms });
    // A shade past the animation itself, so the last of it has landed before
    // the element goes.
    pourTimer.current = window.setTimeout(() => setPour(null), pourStages(ms).total + 400);
  };
  useEffect(() => () => { if (pourTimer.current !== null) window.clearTimeout(pourTimer.current); }, []);

  const [contextMenu, setContextMenu] = useState<{ index: number; x: number; y: number } | null>(null);
  /** The swirl playing right now, if any — transient, like `pour`. The token
   *  is what restarts the animation when the same vessel is swirled twice. */
  const [swirl, setSwirl] = useState<{ index: number; token: number } | null>(null);
  const [standMenu, setStandMenu] = useState<{ columnBase: number; x: number; y: number } | null>(null);
  // Pending settle timers, keyed by container id rather than index — a
  // container's index moves when another is added or removed, its id doesn't.
  const settleTimers = useRef<Map<string, number>>(new Map());
  // Read inside those timers, which fire long after the render that set them.
  const containersRef = useRef(containers);
  containersRef.current = containers;

  useEffect(() => {
    const timers = settleTimers.current;
    return () => { timers.forEach(id => window.clearTimeout(id)); };
  }, []);
  const selected = containers[selectedIndex];
  // The same layout the scene draws from — the context menu has to know
  // whether a burner could physically reach a given vessel (a tube in a
  // wooden rack, or one clamped above another, is out of a flame's reach).
  const { columns, placements } = benchLayout(containers.map(c => ({
    vesselType: c.vesselType,
    standType: c.standType,
  })));
  // Sound follows the SELECTED container only (not every boiling flask on
  // the bench at once) and only while it actually has something in it —
  // matches the scene's own "don't light an empty flask" rule.
  // Boiling because the burner drove it there, or because the reaction did —
  // the sound follows whatever the bench is drawing.
  useBoilingSound(selected.contents.length > 0 && isBoilingNow(
    selected.temperatureC, selected.outcome?.boilingPointC,
    selected.outcome?.temperatureChange?.band, selected.burnerType !== 'none',
  ));

  function updateContainer(index: number, patch: Partial<LabContainer>): void {
    setContainers(prev => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  // ── The guided run ───────────────────────────────────────────────────────
  // Free-form is everything below with `experiment` absent: the gate lets every
  // action through and none of this state is read.
  const [cursor, setCursor] = useState(0);
  const [correction, setCorrection] = useState<string | undefined>(undefined);
  /** Which log entry each finished instruction produced, so the panel can show
   *  that step's result under it. Keyed by instruction index. */
  const [doneStepIds, setDoneStepIds] = useState<Record<number, string>>({});
  // Read inside the burner's settle timer, which fires long after the render
  // that set it — see scheduleSettledRecompute.
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  /**
   * The gate every bench action passes through.
   *
   * Returns the id to log the action under when it is allowed, and null when it
   * is refused — so a caller reads as `const id = allow(...); if (!id) return;`
   * and cannot accidentally act on a refusal. Free-form never refuses.
   */
  function allow(attempt: BenchAttempt): string | null {
    const id = newStepId();
    if (!experiment) {
      // Recording: what the teacher does is the script being written, and
      // what cannot be a step is refused the way a guided run refuses it.
      const rec = recordingRef.current;
      if (rec) {
        const made = recordAttempt(rec.steps, attempt, vesselNames(rec.start), i => containers[i].vesselType);
        if ('why' in made) { setRecordingWhy(made.why); return null; }
        setRecording({ ...rec, steps: made.steps });
        setRecordingWhy(undefined);
      }
      return id;
    }
    const step = experiment.steps[cursor];
    if (!step) {
      setCorrection('This experiment is finished — run it again, or open the free-form bench.');
      return null;
    }
    const verdict = judge(step, attempt);
    if (!verdict.ok) {
      setCorrection(verdict.message);
      return null;
    }
    advance(id);
    return id;
  }

  /** Mark the current instruction carried out and move to the next. */
  function advance(logStepId?: string): void {
    setCorrection(undefined);
    if (logStepId) setDoneStepIds(prev => ({ ...prev, [cursorRef.current]: logStepId }));
    setCursor(c => c + 1);
  }

  // The step's vessel is selected for the student, so "wrong beaker" is never
  // a way to fail — which is what lets judge() ignore the container entirely.
  useEffect(() => {
    if (!experiment) return;
    setSelectedIndex(stepContainer(experiment, cursor));
  }, [experiment, cursor]);

  /** Back to the first instruction on a fresh bench. */
  function restartExperiment(): void {
    openBench(start);
  }

  /**
   * A fresh bench and a fresh session: the saved one's glassware and contents,
   * or the experiment's own (or the free bench's) when there is none.
   */
  function openBench(vessels: ChemistrySetupVessel[] | null): void {
    settleTimers.current.forEach(id => window.clearTimeout(id));
    settleTimers.current.clear();
    sessionIdRef.current = null;
    setContainers(vessels ? containersFromSetup(vessels) : startingContainers(experimentProp, quantitative));
    setSelectedIndex(0);
    setCursor(0);
    setCorrection(undefined);
    setDoneStepIds({});
    setContextMenu(null);
    setStandMenu(null);
    setPendingAdd(null);
    if (vessels) setOpenToken(t => t + 1);
  }

  // `step` logs this action to the container's running log (StepLog, the right
  // panel) once it resolves — omitted for actions that shouldn't appear there
  // (a burner being removed, say, which the log has nothing to say about).
  /**
   * The bench this run belongs to. Opened on the first mix, not on page load —
   * a session is a metered thing and arriving on the page should not spend one.
   * Cleared when the bench is, so the next mix starts a fresh session.
   */
  /**
   * The bench's own light/dark choice, independent of the page around it.
   *
   * The page follows the reader's system setting, which is right for a document
   * — but a bench is a workspace, and which ground the glassware reads best
   * against is a working preference, not a reading one. Someone on a dark site
   * may still want the pale bench to judge a precipitate's colour against.
   * `null` means "follow the page", which stays the default.
   */
  const [benchTheme, setBenchTheme] = useState<'light' | 'dark' | null>(() => {
    try {
      const v = localStorage.getItem('chemlab.benchTheme');
      return v === 'light' || v === 'dark' ? v : null;
    } catch {
      return null;
    }
  });

  /** What the bench is actually showing: the chosen lighting, or the page's. */
  const benchShowing: 'light' | 'dark' = benchTheme
    ?? (typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

  function toggleBenchTheme(): void {
    // Resolve what is actually on screen before flipping, so the first tap
    // always visibly changes something rather than setting what is already set.
    const next = benchShowing === 'dark' ? 'light' : 'dark';
    setBenchTheme(next);
    try { localStorage.setItem('chemlab.benchTheme', next); } catch { /* not persisted */ }
  }

  /**
   * How big the lab is drawn — the chrome through `--chemlab-ui-scale`, the
   * bench's own text through the same number (see ChemistryLabScene2D).
   *
   * It STARTS from the width of the window, because that is the only thing
   * about the display CSS can actually see: a 14-inch laptop hands us around
   * 1440 CSS pixels and a 27-inch monitor at native resolution hands us 2560,
   * and type tuned for the first is small on the second. It is not a rule,
   * only a starting point — a projector mirrors the laptop, so it reports the
   * laptop's width while being read from the back of a hall, and no measure of
   * the viewport can know that. That is what A- / A+ are for, and once they
   * are used the choice is remembered and the width stops deciding.
   */
  const [uiScale, setUiScale] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem('chemlab.uiScale'));
      if (saved >= UI_SCALE_STEPS[0] && saved <= UI_SCALE_STEPS[UI_SCALE_STEPS.length - 1]) return saved;
    } catch { /* not stored */ }
    return scaleForWidth(typeof window === 'undefined' ? 0 : window.innerWidth);
  });

  /**
   * How closely the reagents are packed, on a phone.
   *
   * A phone has one real choice, not six sizes: fit more bottles on a screen,
   * or give each one room for its whole formula — Pb(NO3)2(aq) is 94px at
   * 13px and a tight grid gives it about 60. The same A / A pair asks it, so
   * the control means "smaller / bigger" wherever it is; the stylesheet shows
   * whichever pair suits the width, as it does for the two trays.
   */
  const [phoneDensity, setPhoneDensity] = useState<'compact' | 'roomy'>(() => {
    try { return localStorage.getItem('chemlab.phoneDensity') === 'compact' ? 'compact' : 'roomy'; }
    catch { return 'roomy'; }
  });
  function choosePhoneDensity(next: 'compact' | 'roomy'): void {
    setPhoneDensity(next);
    try { localStorage.setItem('chemlab.phoneDensity', next); } catch { /* not persisted */ }
  }

  function stepUiScale(direction: 1 | -1): void {
    const i = UI_SCALE_STEPS.indexOf(nearestStep(uiScale));
    const next = UI_SCALE_STEPS[Math.min(UI_SCALE_STEPS.length - 1, Math.max(0, i + direction))];
    setUiScale(next);
    try { localStorage.setItem('chemlab.uiScale', String(next)); } catch { /* not persisted */ }
  }

  /**
   * How wide the host's title is.
   *
   * The title is centred on the BAR rather than placed after the back button
   * (see .chemlab-title), so anything that belongs beside it has to know how
   * far to stand off centre — and the title is the host's, so its width is
   * whatever it says. Measured rather than guessed: "Chemistry Bench" and
   * "Neutralisation with an indicator" are not the same distance.
   */
  const topbarRef = useRef<HTMLDivElement>(null);
  const [titleWidth, setTitleWidth] = useState(0);
  useLayoutEffect(() => {
    const title = topbarRef.current?.querySelector('.chemlab-title');
    if (!title) return;
    const measure = (): void => setTitleWidth(w => {
      const next = Math.round(title.getBoundingClientRect().width);
      return next === w ? w : next;
    });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    return () => observer.disconnect();
  });

  /** A reagent waiting on a strength before it pours — quantitative bench
   *  only, and only for a solution the catalogue offers a choice for. */
  const [pendingAdd, setPendingAdd] = useState<{ substanceId: string; x: number; y: number } | null>(null);

  const sessionIdRef = useRef<string | null>(null);

  async function ensureSessionId(): Promise<string> {
    if (sessionIdRef.current) return sessionIdRef.current;
    const session = await api.startSession(quantitative ? 'quantitative' : 'free');
    sessionIdRef.current = session.sessionId;
    return session.sessionId;
  }

  async function recompute(
    index: number, substanceIds: string[], temperatureBand: TemperatureBand,
    step?: { label: string; labelWithAmount?: string; kind?: LabStep['kind']; id?: string }, arrivingGasId?: string,
    phase?: 'liquid' | 'vapor',
  ): Promise<void> {
    // A gas piped in from another container counts as contents: limewater with
    // CO2 bubbling through it is a two-substance mix, not limewater alone.
    // Passed explicitly when the source has just resolved (its new outcome
    // isn't in state yet); read from state otherwise.
    const gasId = arrivingGasId ?? incomingGasId(index, containersRef.current);
    const inputs = gasId ? [...substanceIds, gasId] : substanceIds;
    if (inputs.length === 0) {
      updateContainer(index, { outcome: undefined, outcomeSource: undefined, error: undefined, loading: false });
      return;
    }
    // One substance sitting at room temperature reacts with nothing, and what it
    // *is* — acidic, basic, what the pH paper reads — is a fact in the catalogue both
    // sides now share. Answer it here: no request, and so no pending state for
    // something that was never being computed. Skipped for a vapor-phase call
    // (a rod's reagent meeting a gas already IN this vessel) — `phase` is only
    // ever passed alongside a second substance (see its call sites), so this
    // never actually fires for one, but the guard keeps the invariant explicit.
    const lone = !phase && inputs.length === 1 && temperatureBand === 'room' ? findSubstance(inputs[0]) : undefined;
    if (lone) {
        const outcome = intrinsicOutcomeFor(lone);
        setContainers(prev => prev.map((c, i) => (i !== index ? c : {
            ...c,
            outcome, outcomeSource: 'intrinsic', loading: false, error: undefined,
            outcomeBand: temperatureBand,
            steps: step
                ? [...c.steps, {
                    id: step.id ?? newStepId(), label: step.label, kind: step.kind ?? 'action',
                    // The same sentence the server path stores. A lone substance
                    // is answered here from the catalogue, and this branch was
                    // the second copy that dropped it — so the FIRST add of
                    // anything never said how much went in.
                    labelWithAmount: step.labelWithAmount,
                    outcome, source: 'intrinsic', temperatureC: c.temperatureC,
                }]
                : c.steps,
        })));
        return;
    }

    updateContainer(index, { loading: true, error: undefined, outcomeBand: temperatureBand });
    if (step) showStepsOnPhone();
    try {
      const sessionId = await ensureSessionId();
      // The ids are what the chemistry is asked in; the amounts ride along for
      // the quantitative bench to compute from. A substance that is not in the
      // vessel's own contents — a rod's reagent, a gas that arrived through a
      // tube — has no amount here, and the arithmetic says so rather than
      // treating it as none.
      const held = containersRef.current[index]?.contents ?? [];
      const contents: VesselContentInput[] = inputs.map(id => (
        held.find(h => h.substanceId === id) ?? { substanceId: id, qty: 0 }
      ));
      const result = await api.action(contents, temperatureBand, 'add-substance', sessionId, phase);
      // Tagged client-side, never sent by the backend — see types.ts's
      // `phase` doc for why the renderer needs this to say "Dense White
      // Fumes" instead of "White Precipitate" for a vapor-phase result.
      const outcome = phase === 'vapor' ? { ...result.outcome, phase } : result.outcome;
      // Functional update (not updateContainer, which would close over a
      // possibly-stale `containers`) — steps must append onto whatever the
      // container's log actually is by the time this promise resolves.
      setContainers(prev => prev.map((c, i) => (i !== index ? c : {
        ...c,
        outcome,
        outcomeSource: result.source,
        loading: false,
        steps: step
          ? [...c.steps, {
            id: step.id ?? newStepId(), label: step.label, kind: step.kind ?? 'action',
            // Composed by the caller and, until now, dropped on the floor here
            // — which is why a bench showing quantities showed none of them.
            labelWithAmount: step.labelWithAmount,
            outcome, source: result.source, temperatureC: c.temperatureC,
            stoichiometry: result.stoichiometry,
            stoichiometryUnavailable: result.stoichiometryUnavailable,
          }]
          : c.steps,
      })));
      pushGasDownPipe(index, result.outcome.gasEvolution?.gas, result.stoichiometry);
    } catch (err) {
      updateContainer(index, {
        loading: false,
        error: err instanceof Error ? err.message : 'Something went wrong computing that mix.',
        // Nothing was computed, so nothing is current — otherwise a retry at
        // the same temperature would be dismissed as "already have that".
        outcomeBand: undefined,
      });
    }
  }

  /** The gas reaching `index` through a tube, from whatever is piped into it. */
  function incomingGasId(index: number, list: LabContainer[]): string | undefined {
    const source = list.find(c => c.pipeToId === list[index]?.id);
    const gas = source?.outcome?.gasEvolution?.gas;
    return gas ? GAS_SUBSTANCE_IDS[gas] : undefined;
  }

  /*
   * A saved bench has been set out: work out what is in each vessel again, one
   * at a time so they share the one session. A single reagent at room
   * temperature is answered from the catalogue and costs nothing; a tube
   * carries its gas on as the vessel it leads from resolves.
   */
  useEffect(() => {
    if (!openToken) return;
    let live = true;
    void (async () => {
      const list = containersRef.current;
      for (let i = 0; i < list.length && live; i++) {
        const c = list[i];
        if (c.contents.length === 0) continue;
        const names = c.contents.map(x => findSubstance(x.substanceId)?.displayName ?? x.substanceId);
        await recompute(i, substanceIdsOf(c), bandFor(c.temperatureC), { label: `Set out with ${names.join(', ')}` });
      }
    })();
    return () => { live = false; };
    // Once per bench set out; recompute reads the bench through its ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openToken]);

  // ── Saving, picking, recording (teacher app) ─────────────────────────────
  const currentIsScript = !!setups.current?.saved.steps?.length;
  const benchNow = (): ChemistrySetupVessel[] => benchSetup(containers);
  /** The free bench differs from the setup it was opened as. A guided run is
   *  never "unsaved": what changes on it is the run, not the experiment. */
  const unsaved = setups.enabled && !!setups.current && !script && !recording && !currentIsScript
    && !sameSetup(benchNow(), setups.current.saved.vessels);

  async function pickSetup(id: string): Promise<void> {
    const payload = await setups.load(id);
    if (!payload) return;
    setRecording(null);
    setRecordingWhy(undefined);
    setStart(payload.vessels);
    setScript(payload.steps?.length ? payload : null);
    openBench(payload.vessels);
  }

  /** Off the saved setup or experiment, onto an empty free bench. */
  function leaveSetup(): void {
    setRecording(null);
    setRecordingWhy(undefined);
    setScript(null);
    setStart(null);
    setups.leave();
    openBench(null);
  }

  function saveNow(): void {
    if (!setups.enabled || setups.busy) return;
    if (recordingRef.current) { stopRecording(); return; }
    if (script) return;
    // Saving over an experiment from the free bench would throw its steps away.
    if (setups.current && !currentIsScript) void setups.save({ payload: { vessels: benchNow() } });
    else setSaveDialog('new');
  }
  const saveRef = useRef(saveNow);
  saveRef.current = saveNow;
  useEffect(() => {
    if (!setups.enabled) return;
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setups.enabled]);

  async function saveFromDialog(name: string, notes: string, chapterIds?: string[]): Promise<void> {
    // Save as… while a guided experiment plays copies the experiment; on the
    // free bench, it saves the bench.
    const payload: ChemistrySetupPayload = script ?? { vessels: benchNow() };
    const ok = await setups.save({ payload, name, notes, chapterIds, asNew: true });
    if (ok) {
      setSaveDialog(null);
      if (!script) setStart(payload.vessels);
    }
  }

  /** Recording from the bench as it stands, or from a saved starting bench. */
  function startRecording(from?: ChemistrySetupVessel[]): void {
    setRecording({ start: from ?? benchNow(), steps: [] });
    setRecordingWhy(undefined);
    setSheet('notes');
  }

  function stopRecording(): void {
    const rec = recordingRef.current;
    if (!rec) return;
    setRecording(null);
    if (rec.steps.length === 0) { setRecordingWhy(undefined); return; }
    setEditor({ vessels: rec.start, steps: rec.steps, conclusion: setups.current?.saved.conclusion, from: 'recording' });
  }

  function cancelEditor(): void {
    const ed = editor;
    setEditor(null);
    // Back to recording, with what was recorded: closing the editor is not
    // throwing the recording away.
    if (ed?.from === 'recording') setRecording({ start: ed.vessels, steps: ed.steps });
  }

  async function saveExperiment(payload: ChemistrySetupPayload, name: string, notes: string, chapterIds: string[], asNew: boolean): Promise<void> {
    const ok = await setups.save({ payload, name, notes, chapterIds, asNew: asNew || !setups.current });
    if (!ok) return;
    setEditor(null);
    // Straight into the run, from the top — the experiment as a class will see it.
    setStart(payload.vessels);
    setScript(payload);
    openBench(payload.vessels);
  }

  function editSteps(): void {
    if (!script) return;
    setEditor({
      vessels: script.vessels, steps: toRecorded(script.steps ?? [], vesselNames(script.vessels)),
      conclusion: script.conclusion, from: 'saved',
    });
  }

  /** Record the experiment again, from its starting bench, on the free bench. */
  function recordAgain(): void {
    const from = editor?.vessels ?? script?.vessels ?? start;
    setEditor(null);
    setScript(null);
    if (from) { setStart(from); openBench(from); }
    startRecording(from ?? undefined);
  }

  /** Whether every one of `ids` is a catalogued gas — a vessel holding
   *  nothing but arrived/evolved gas, no real liquid. The condition for a
   *  dipped rod to react with a vessel's own contents (see handleDipGlassRod
   *  and deliverGasTo): a rod resting in an ordinary liquid mix is just
   *  apparatus, but a rod colocated with pure gas is the ammonia/HCl "dense
   *  white fumes" test, whichever order the rod and the gas arrived in. */
  function isGasOnlyMix(ids: string[]): boolean {
    return ids.length > 0 && ids.every(id => findSubstance(id)?.category === 'gas');
  }

  /** Delivers `gas` (if any) into container `destId`'s own contents and asks
   *  for a fresh answer there. Takes the destination's ID directly rather
   *  than looking up a just-set pipeToId from containersRef — a caller that
   *  JUST called updateContainer to set that pipeToId (handleConnectPipe)
   *  would read its own not-yet-committed change back as stale, since React
   *  hasn't re-rendered between the two calls yet. */
  function deliverGasTo(destId: string, gas: GasId | undefined, mol?: number): void {
    const destIndex = containersRef.current.findIndex(c => c.id === destId);
    if (destIndex === -1) return;
    const dest = containersRef.current[destIndex];
    const gasId = gas ? GAS_SUBSTANCE_IDS[gas] : undefined;
    if (!gasId) return; // nothing evolving right now, nothing arrives
    // The same gas still evolving (e.g. the source recomputes again because
    // more of the same acid was added) shouldn't pile up as repeated
    // identical entries in the contents label — only genuinely new arrivals
    // (a different gas, or this one again after something else came through)
    // add another entry.
    if (dest.contents[dest.contents.length - 1]?.substanceId === gasId) return;
    // The gas has to actually join the contents, the same way handleAddSubstance
    // does for anything poured in by hand — passing it to recompute alone
    // (as an earlier version did) fed it into that ONE outcome call without
    // ever recording it as part of the vessel's contents, so the label kept
    // reading "Empty" and the gas vanished again on the container's next
    // recompute (arrivingGasId only covers the call it's passed to).
    const nextContents = addToContents(dest.contents, gasId, dest.vesselType)
      // A gas has no dose to scale, so `addToContents` gives it qty 0. Its
      // amount is the moles that were made next door.
      .map(c => (c.substanceId === gasId && mol !== undefined ? { ...c, mol } : c));
    const label = `${findSubstance(gasId)?.displayName ?? 'Gas'} arrived through the tube`;
    updateContainer(destIndex, { contents: nextContents });
    // A rod already resting here, with nothing but gas in the vessel, meets
    // that gas the instant it arrives — same ammonia/HCl fumes test as
    // handleDipGlassRod's own check below, just the opposite order (the rod
    // got here first). Isn't recorded in the contents — the rod's reagent
    // isn't part of the vessel's own contents, only what reacted with what
    // arrived — so a later gas arrival still starts fresh from the contents.
    const nextIds = substanceIdsOf({ contents: nextContents });
    const rodId = dest.glassRodSubstanceId;
    const isVapor = !!rodId && isGasOnlyMix(nextIds);
    const mixInputs = isVapor ? [...nextIds, rodId!] : nextIds;
    // nextIds already carries the gas — '' (not omitted) skips
    // recompute's OWN incomingGasId fallback, which would otherwise
    // re-derive and append the same gas a second time.
    void recompute(destIndex, mixInputs, bandFor(dest.temperatureC), { label }, '', isVapor ? 'vapor' : undefined);
  }

  /** A container's outcome has just changed; if a tube leads out of it, the
   *  vessel at the far end has new contents and needs its own answer. Safe
   *  to read pipeToId from the ref here — unlike handleConnectPipe, this
   *  always runs after an `await` (inside recompute), by which point React
   *  has had a chance to commit and re-render any earlier state changes. */
  function pushGasDownPipe(index: number, gas: GasId | undefined, working?: Stoichiometry): void {
    const pipeToId = containersRef.current[index]?.pipeToId;
    if (!pipeToId) return;
    // On the quantitative bench the gas arrives with an AMOUNT: the moles this
    // vessel's own arithmetic just computed for it. That is what makes "the CO2
    // from 1 g of marble chips, into limewater" a question with a number in it
    // rather than a picture of one — and it is why a piped gas is the one
    // content whose quantity is not a dose.
    const product = gas && working
      ? working.products.find(p => p.volumeL !== null)
      : undefined;
    deliverGasTo(pipeToId, gas, product?.mol);
  }

  /** Whether `toId` already leads back to `fromId`, so connecting them would
   *  make a loop that recomputes itself forever. */
  function pipeLoops(fromId: string, toId: string, list: LabContainer[]): boolean {
    const seen = new Set<string>();
    let at: string | undefined = toId;
    while (at && !seen.has(at)) {
      if (at === fromId) return true;
      seen.add(at);
      at = list.find(c => c.id === at)?.pipeToId;
    }
    return false;
  }

  function handleConnectPipe(index: number, toId: string): void {
    const from = containers[index];
    if (from.id === toId || pipeLoops(from.id, toId, containers)) return;
    const stepId = allow({ kind: 'connect-pipe', container: index, toContainer: containers.findIndex(c => c.id === toId) });
    if (!stepId) return;
    updateContainer(index, { pipeToId: toId });
    logStep(index, `Ran a delivery tube to ${containerLabel(toId)}`, stepId);
    deliverGasTo(toId, from.outcome?.gasEvolution?.gas);
  }

  function handleDisconnectPipe(index: number): void {
    const c = containers[index];
    if (!c.pipeToId) return;
    const destIndex = containers.findIndex(x => x.id === c.pipeToId);
    updateContainer(index, { pipeToId: undefined });
    // The far end loses its gas, so its answer is no longer the right one.
    if (destIndex !== -1) {
      const dest = containers[destIndex];
      void recompute(destIndex, substanceIdsOf(dest), bandFor(dest.temperatureC), { label: 'Delivery tube disconnected' }, '');
    }
  }

  /** How a container reads in a menu or a step: what's in it, else its shape. */
  function containerLabel(id: string): string {
    const c = containers.find(x => x.id === id);
    if (!c) return 'another container';
    const names = c.contents.map(x => findSubstance(x.substanceId)?.displayName).filter(Boolean);
    return names.length > 0 ? names.join(' + ') : `the empty ${VESSEL_LABELS[c.vesselType].toLowerCase()}`;
  }

  /**
   * A swirl of the vessel — what a hand at a real bench does between every
   * other act, and the bench had no way to express.
   *
   * It carries NO chemistry: no contents change, no step is spent, no request
   * is made, and the outcome it already has stays exactly as it was. What it
   * changes is how the contents are ARRANGED — see LabContainer.swirls — and
   * it goes in the log, so a guided procedure can ask for it.
   */
  function handleSwirl(index: number): void {
    const c = containers[index];
    if (c.contents.length === 0) return;
    // A swirl cannot be wrong, so it does not go through allow(): during a
    // guided run it advances the script when the step asks for one, and is
    // simply allowed otherwise. Refusing a student for mixing — which is what
    // routing it through allow() would do on every other step — teaches the
    // opposite of what the gesture is for.
    if ((experiment && experiment.steps[cursor]?.expect.kind === 'swirl') || (!experiment && recordingRef.current)) {
      allow({ kind: 'swirl', container: index });
    }
    updateContainer(index, {
      swirls: c.swirls + 1,
      steps: [...c.steps, { id: newStepId(), label: 'Swirled it', kind: 'action' }],
    });
    setSwirl({ index, token: Date.now() });
  }

  /**
   * A reagent has been clicked. On the quantitative bench, a solution with a
   * choice of strengths asks for one before it pours — picking it is the
   * exercise. Everything else, and the whole free bench, pours on the click.
   */
  function handleAddSubstance(substanceId: string, at?: { x: number; y: number }): void {
    const substance = findSubstance(substanceId);
    // Asked whichever way the reagent was reached. It used to require `at`, so
    // a caller that did not report a position — the title-bar search — poured
    // straight in at the catalogue's strength and never asked.
    if (quantitative && substance?.molarities?.length) {
      const anchor = at ?? { x: window.innerWidth / 2 - 110, y: 120 };
      setPendingAdd({ substanceId, x: anchor.x, y: anchor.y });
      return;
    }
    addSubstance(substanceId);
  }

  function addSubstance(substanceId: string, concentrationM?: number): void {
    // Checked BEFORE allow(), which is what spends a step in a guided run: a
    // pour that never happened must not count as an attempt at one.
    const target = containers[selectedIndex];
    if (!hasRoomFor(target, substanceId)) {
      const vessel = VESSEL_LABELS[target.vesselType].toLowerCase();
      updateContainer(selectedIndex, {
        error: `The ${vessel} is full — clear it, or start again in something bigger.`,
      });
      return;
    }
    const nextContents = addToContents(target.contents, substanceId, target.vesselType, concentrationM);
    const stepId = allow({
      kind: 'add-substance', container: selectedIndex, substanceId,
      // What the vessel will hold once this goes in — a step asking for an
      // amount is judged against the total, so a second helping carries on
      // towards it rather than failing the step again.
      qty: nextContents.find(x => x.substanceId === substanceId)?.qty ?? 0,
    });
    if (!stepId) return;
    const c = target;
    const substance = findSubstance(substanceId);
    // Contents changed — any prior pH paper dip was for a mixture that no
    // longer exists. The rod is NOT touched: what it carries is chosen when
    // it's dipped, and pouring something into the vessel it rests in doesn't
    // re-dip it.
    updateContainer(selectedIndex, { contents: nextContents, phPaperApplied: false });
    if (substance) {
      showPour(
        selectedIndex,
        substanceId,
        !c.contents.some(x => {
          const held = findSubstance(x.substanceId);
          return held?.physicalState === 'liquid' || held?.physicalState === 'aqueous';
        }),
        pourDurationFor(substance, c.vesselType),
      );
    }
    // The amount goes in the log, not in the request: what a step says is what
    // this act put in ("Added 2 drops of Methyl Orange"), composed here from
    // state the client holds. The chemistry is asked in ids alone — see
    // substanceIdsOf, and chemistry-lab-dosing-target.md for why a quantity
    // that is not in the mix key must not reach the prompt either.
    const amount = substance ? formatAmount(substance, perAddFor(substance, c.vesselType)) : '';
    const name = substance?.displayName ?? substanceId;
    // The strength is named only when the student chose it. Printing the
    // catalogue's own value on every line would make a choice look as though
    // it had been made where none was offered.
    const strength = concentrationM ? ` ${formatMolarity(concentrationM)}` : '';
    // A counted solid names its mass on the quantitative bench, as a solution
    // names its strength: "3 chips" is not an amount anyone can calculate with.
    const mass = quantitative && substance ? countedMass(substance, perAddFor(substance, c.vesselType)) : null;
    const counted = mass ? `${amount} (${mass})` : amount;
    void recompute(
      selectedIndex, substanceIdsOf({ contents: nextContents }), bandFor(c.temperatureC),
      {
        label: `Added ${name}`,
        labelWithAmount: amount ? `Added ${counted} of${strength} ${name}` : undefined,
        id: stepId,
      },
    );
  }

  // Addressed by index, not "the selected container": the slider lives on the
  // bench next to its own burner, so it acts on that burner's container.
  // The reading moves at once — the control has to feel live — while the
  // chemistry waits for the slider to settle.
  function handleSetTemperature(index: number, celsius: number): void {
    const c = containers[index];
    if (c.burnerType === 'none') return; // nothing to heat it with
    if (celsius === c.temperatureC) return;
    updateContainer(index, { temperatureC: celsius });
    scheduleSettledRecompute(c.id);
  }

  function scheduleSettledRecompute(containerId: string): void {
    const timers = settleTimers.current;
    const pending = timers.get(containerId);
    if (pending) window.clearTimeout(pending);
    timers.set(containerId, window.setTimeout(() => {
      timers.delete(containerId);
      const index = containersRef.current.findIndex(c => c.id === containerId);
      if (index === -1) return; // removed from the bench while we waited
      const c = containersRef.current[index];
      const band = bandFor(c.temperatureC);
      // The slider is continuous but the chemistry only knows three bands, so
      // a temperature that lands back in the band the current outcome was
      // computed at needs no new answer.
      if (band === c.outcomeBand) return;
      // A heat step completes when the mixture actually gets hot, not when the
      // slider is dragged: the control is continuous and crosses bands on the
      // way to wherever it is going. See guidedRun's own note.
      const heatStep = experiment?.steps[cursorRef.current];
      const stepId = heatStep && reachedByHeat(heatStep, band) ? newStepId() : undefined;
      if (stepId) advance(stepId);
      // Recording takes the band the burner settled in — warm, or boiling.
      const rec = recordingRef.current;
      if (!experiment && rec && band !== 'room') {
        setRecording({ ...rec, steps: recordHeat(rec.steps, index, band, vesselNames(rec.start)) });
      }
      updateContainer(index, { phPaperApplied: false });
      void recompute(index, substanceIdsOf(c), band, { label: temperatureStepLabel(c.temperatureC), id: stepId });
    }, TEMPERATURE_SETTLE_MS));
  }

  /** Dipping and taking the paper back out are the same control — a strip you
   *  can put in but never remove is a strip stuck in the vessel forever. */
  function handleToggleTest(): void {
    const c = containers[selectedIndex];
    // Taking the paper back out undoes a reading; it is not an action a script
    // ever asks for, and refusing it would leave a strip stuck in the vessel.
    if (c.phPaperApplied) {
      updateContainer(selectedIndex, { phPaperApplied: false });
      return;
    }
    const stepId = allow({ kind: 'dip-test', container: selectedIndex });
    if (!stepId) return;
    updateContainer(selectedIndex, { phPaperApplied: true });
    void recompute(selectedIndex, substanceIdsOf(c), bandFor(c.temperatureC), { label: 'Dipped pH Paper', kind: 'test', id: stepId });
  }

  function handleClearContainer(index: number): void {
    if (!allow({ kind: 'off-script', what: 'emptying a vessel' })) return;
    updateContainer(index, {
      contents: [], outcome: undefined, outcomeSource: undefined, outcomeBand: undefined,
      error: undefined, phPaperApplied: false, glassRodSubstanceId: null, hasGlassRod: false, steps: [],
    });
  }

  /** Back to a fresh bench. Everything here is client-held (labState.ts), so
   *  this is a reset, not a delete of anything that outlives the session. */
  function handleClearBench(): void {
    if (experiment) { restartExperiment(); return; }
    if (recordingRef.current) {
      setRecordingWhy('While recording, the bench stays as it was when you started. Stop recording to clear it.');
      return;
    }
    settleTimers.current.forEach(id => window.clearTimeout(id));
    settleTimers.current.clear();
    // A session is one run of the bench from clear, so clearing ends it.
    sessionIdRef.current = null;
    setContainers(initialContainers(quantitative));
    setSelectedIndex(0);
    setContextMenu(null);
    setStandMenu(null);
    setPendingAdd(null);
  }

  /** A rod is only useful once it has been dipped in something, so the student
   *  says what — the classic test is conc. HCl on a rod held over ammonia, and
   *  which reagent is on it is the whole point of the test. */
  function handleDipGlassRod(substanceId: string): void {
    const stepId = allow({ kind: 'dip-rod', container: selectedIndex, substanceId });
    if (!stepId) return;
    const substance = findSubstance(substanceId);
    const c = containers[selectedIndex];
    updateContainer(selectedIndex, { hasGlassRod: true, glassRodSubstanceId: substanceId });
    const label = `Dipped the Glass Rod in ${substance?.displayName ?? substanceId}`;
    // Dipping into a vessel that ALREADY holds nothing but gas (arrived
    // through a tube before the rod got here) is the other order of the same
    // fumes test deliverGasTo checks for — see isGasOnlyMix's doc.
    if (isGasOnlyMix(substanceIdsOf(c))) {
      void recompute(selectedIndex, [...substanceIdsOf(c), substanceId], bandFor(c.temperatureC), { label, kind: 'test', id: stepId }, '', 'vapor');
    } else {
      logStep(selectedIndex, label, stepId);
    }
  }

  function handleRemoveGlassRod(): void {
    updateContainer(selectedIndex, { hasGlassRod: false, glassRodSubstanceId: null });
  }

  /** The classic ammonia/HCl "dense white fumes" test: a rod carrying one
   *  reagent, held near another container's escaping gas. A one-shot test,
   *  like a pH paper dip — it reports what the rod's tip saw without touching
   *  the rod's OWN container's contents; the rod sniffed the fumes, it
   *  wasn't poured into anything. */
  async function handleHoldRodNear(index: number, targetId: string): Promise<void> {
    const c = containers[index];
    const targetIndex = containers.findIndex(t => t.id === targetId);
    const gas = containers[targetIndex]?.outcome?.gasEvolution?.gas;
    const gasSubstanceId = gas ? GAS_SUBSTANCE_IDS[gas] : undefined;
    if (!c.glassRodSubstanceId || !gasSubstanceId || targetIndex === -1) return;
    const stepId = allow({ kind: 'rod-near-gas', container: index, toContainer: targetIndex });
    if (!stepId) return;
    const label = `Held the rod near ${containerLabel(targetId)}`;
    updateContainer(index, { loading: true, error: undefined });
    try {
      const sessionId = await ensureSessionId();
      const result = await api.action(
        [{ substanceId: c.glassRodSubstanceId, qty: 0 }, { substanceId: gasSubstanceId, qty: 0 }],
        'room', 'apply-test', sessionId, 'vapor',
      );
      // Tagged client-side — see recompute's own matching comment and
      // types.ts's `phase` doc.
      const outcome = { ...result.outcome, phase: 'vapor' as const };
      // Functional update, same reasoning as recompute's — and deliberately
      // NOT `outcome`/`contents`: this container's own liquid didn't
      // change, only its rod's tip reacted with something nearby.
      setContainers(prev => prev.map((row, i) => (i !== index ? row : {
        ...row,
        loading: false,
        steps: [...row.steps, {
          id: stepId, label, kind: 'test', outcome, source: result.source, temperatureC: row.temperatureC,
        }],
      })));
    } catch (err) {
      updateContainer(index, {
        loading: false,
        error: err instanceof Error ? err.message : 'Something went wrong testing that.',
      });
    }
  }

  /** Record an action that changed the apparatus but not the chemistry, so it
   *  needs no outcome and no request. */
  function logStep(index: number, label: string, id?: string): void {
    setContainers(prev => prev.map((c, i) => (
      i === index ? { ...c, steps: [...c.steps, { id: id ?? newStepId(), label, kind: 'action' as const }] } : c
    )));
  }

  /** Put a container on the bench and select it, so the stand/burner rows
   *  address what was just placed. */
  function placeContainer(container: LabContainer): void {
    if (!allow({ kind: 'off-script', what: 'adding glassware' })) return;
    if (containers.length >= MAX_CONTAINERS) return;
    setContainers(prev => [...prev, container]);
    setSelectedIndex(containers.length);
  }

  function handlePlaceVessel(vesselType: VesselType): void {
    placeContainer(newContainer(vesselType));
  }

  /** A stand is no use empty, so it arrives carrying the glassware it holds. */
  function handlePlaceStand(standType: StandType): void {
    placeContainer({ ...newContainer(STAND_HOME_VESSEL[standType]), standType });
  }

  /** Add a vessel to a stand already on the bench. It has to join that stand's
   *  RUN of containers — benchLayout groups neighbours onto one stand — so it
   *  is inserted right after the run rather than appended to the bench. */
  function handleAddToStand(columnBase: number, vesselType: VesselType): void {
    if (!allow({ kind: 'off-script', what: 'adding glassware' })) return;
    if (containers.length >= MAX_CONTAINERS) return;
    const standType = containers[columnBase].standType;
    const insertAt = columnBase + standOccupancy(columnBase);
    setContainers(prev => {
      const next = [...prev];
      next.splice(insertAt, 0, { ...newContainer(vesselType), standType });
      return next;
    });
    setSelectedIndex(insertAt);
  }

  /** How many vessels are already on the stand whose run starts at `columnBase`. */
  function standOccupancy(columnBase: number): number {
    return columns.find(members => members[0] === columnBase)?.length ?? 1;
  }

  /** Right-click a container tile: place it CONNECTED — sharing the selected
   *  vessel's stand rather than standing beside it. Both ends go onto the
   *  multi-level rod, which is what benchLayout stacks into one column. */
  function handlePlaceVesselConnected(vesselType: VesselType): void {
    if (!allow({ kind: 'off-script', what: 'adding glassware' })) return;
    if (containers.length >= MAX_CONTAINERS) return;
    const anchor = selectedIndex;
    setContainers(prev => {
      const next = [...prev];
      next[anchor] = { ...next[anchor], standType: 'multi-level' };
      next.splice(anchor + 1, 0, { ...newContainer(vesselType), standType: 'multi-level' });
      return next;
    });
    // The new vessel is clamped ABOVE the anchor, out of any flame's reach —
    // benchLayout enforces that, and the burner row will show it disabled.
    setSelectedIndex(anchor + 1);
  }

  function handleChangeVesselType(index: number, type: VesselType): void {
    if (!allow({ kind: 'off-script', what: 'changing the glassware' })) return;
    const c = containers[index];
    // Not every stand fits every vessel — a tube can't sit in a gauze ring,
    // a beaker has no rack to go in. Reshaping into something the current
    // stand can't hold moves it to that vessel's own default stand.
    const standType = isStandValidFor(type, c.standType) ? c.standType : defaultStandFor(type);
    updateContainer(index, { vesselType: type, standType });
    if (!standAllowsBurner(standType)) cutBurner(index);
  }

  /** Take the flame away and stop any heating that depended on it. */
  function cutBurner(index: number): void {
    const c = containers[index];
    if (c.burnerType === 'none') return;
    updateContainer(index, { burnerType: 'none', temperatureC: ROOM_C });
    if (bandFor(c.temperatureC) !== 'room') void recompute(index, substanceIdsOf(c), 'room');
  }

  function handleChangeBurner(index: number, burnerType: BurnerType): void {
    if (burnerType === 'none') { cutBurner(index); return; }
    if (!allow({ kind: 'attach-burner', container: index })) return;
    const c = containers[index];
    // A flame under a vessel standing flat on the bench would heat the bench,
    // so lighting one stands the vessel on a tripod first. Starts at
    // BOILING_C rather than leaving it at room temperature — a burner that
    // was just lit and is doing nothing until the student also drags the
    // slider reads as the control not working.
    updateContainer(index, { burnerType, standType: standForBurner(c.standType), temperatureC: BOILING_C });
    scheduleSettledRecompute(c.id);
  }

  function handleRemoveContainer(index: number): void {
    if (!allow({ kind: 'off-script', what: 'taking glassware away' })) return;
    // Always keep at least one container on the bench.
    if (containers.length <= 1) return;
    setContainers(prev => prev.filter((_, i) => i !== index));
    setSelectedIndex(prev => {
      if (index < prev) return prev - 1;
      if (index === prev) return Math.min(prev, containers.length - 2);
      return prev;
    });
  }

  // Each finished instruction's log entry, looked up across the whole bench —
  // a guided run's steps land in whichever vessel they acted on.
  const guidedResults: Record<number, LabStep | undefined> = {};
  if (experiment) {
    const logged = containers.flatMap(c => c.steps);
    for (const [index, id] of Object.entries(doneStepIds)) {
      guidedResults[Number(index)] = logged.find(st => st.id === id);
    }
  }

  return (
    <div
      className="chemlab-page bench-palette"
      style={{ ['--chemlab-ui-scale' as string]: uiScale }}
      data-sheet={sheet}
      data-density={phoneDensity}
      /* The pinned fallback for browsers with no element fullscreen — iPhone
         Safari, which is most of the phones this matters on. Same attribute
         and same meaning as SimulationPlayer's. */
      data-full={fullscreen.pinned ? 'true' : undefined}
      ref={pageRef}
    >
      <BenchPhoneHeader
        title={phoneHeader?.title ?? <><BenchMark /> Chemistry Lab</>}
        back={phoneHeader?.back}
        trailing={phoneHeader?.trailing ?? (setups.enabled && !script && !recording ? (
          <button type="button" className="bench-phone-header-btn" onClick={saveNow}
            disabled={setups.busy || (!!setups.current && !currentIsScript && !unsaved)}
            title={setups.current ? 'Save the changes' : 'Save to My setups'} aria-label="Save">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 3h11l3 3v15H5z" /><path d="M8 3v5h7V3" /><path d="M8 21v-7h8v7" />
            </svg>
          </button>
        ) : undefined)}
        theme={benchShowing}
        onToggleTheme={toggleBenchTheme}
        fullscreen={fullscreen}
      />
      <div
        className="chemlab-topbar"
        ref={topbarRef}
        style={{ ['--chemlab-title-w' as string]: `${titleWidth}px` }}
      >
        {topbar ?? <span className="chemlab-title"><BenchMark /> Chemistry Lab</span>}
        {/* Beside the title, and now the bench's only reagent search — the
            shelf's own field between the two rows is gone. This one hands you
            the reagent rather than pointing at where it sits. */}
        <ReagentSearch onAdd={handleAddSubstance} disabled={selected.loading} />
        {setups.enabled && (
          <div className="chemlab-setups">
            <select
              value=""
              aria-label="Open a saved setup"
              title="Open one of your saved setups or experiments"
              disabled={setups.busy || !!recording}
              onChange={e => {
                const v = e.target.value;
                if (v === '__empty') leaveSetup();
                else if (v) void pickSetup(v);
              }}
            >
              <option value="">Setups…</option>
              {setups.mine.length > 0 && (
                <optgroup label="My setups">
                  {setups.mine.map(m => (
                    <option key={m.id} value={m.id}>{m.summary.startsWith('Guided') ? `${m.name} — guided` : m.name}</option>
                  ))}
                </optgroup>
              )}
              <option value="__empty">An empty bench</option>
            </select>
            {setups.current && (
              <span className="chemlab-setup-name" title={setups.current.notes || setups.current.name}>
                {setups.current.name}{unsaved && <span className="chemlab-unsaved" aria-label="unsaved changes"> •</span>}
              </span>
            )}
            {recording ? (
              <button type="button" className="chemlab-rec-on" onClick={stopRecording}
                title="Stop recording, and write the steps up">
                Stop · {recording.steps.length} step{recording.steps.length === 1 ? '' : 's'}
              </button>
            ) : script ? (
              <button type="button" onClick={() => setSaveDialog('copy')} disabled={setups.busy}
                title="Save a copy of this experiment under another name">Save as…</button>
            ) : (
              <>
                <button type="button" onClick={saveNow}
                  disabled={setups.busy || (!!setups.current && !currentIsScript && !unsaved)}
                  title={setups.current && !currentIsScript ? 'Save the changes (⌘S)' : 'Save this bench to My setups (⌘S)'}>
                  {setups.current && !currentIsScript && !unsaved ? 'Saved' : 'Save'}
                </button>
                {setups.current && !currentIsScript && (
                  <button type="button" onClick={() => setSaveDialog('copy')} disabled={setups.busy}>Save as…</button>
                )}
                <button type="button" onClick={() => startRecording()} disabled={setups.busy}
                  title="Record a guided experiment: the bench as it is now is where it starts, and what you do next becomes its steps">
                  ● Record steps
                </button>
              </>
            )}
            {setups.error && <span className="chemlab-setups-error" role="alert">{setups.error}</span>}
          </div>
        )}
        {/* How big the lab is drawn. On the bar rather than on the bench,
            because it acts on the whole page — the bench is one of the things
            it acts on. */}
        <div className="chemlab-textsize chemlab-textsize-wide" role="group" aria-label="Lab size">
          <button
            title="Draw the lab smaller"
            aria-label="Smaller"
            disabled={nearestStep(uiScale) === UI_SCALE_STEPS[0]}
            onClick={() => stepUiScale(-1)}
          >
            A
          </button>
          <button
            title="Draw the lab bigger — for a large screen, or a room reading it from the back"
            aria-label="Bigger"
            disabled={nearestStep(uiScale) === UI_SCALE_STEPS[UI_SCALE_STEPS.length - 1]}
            onClick={() => stepUiScale(1)}
          >
            A
          </button>
        </div>
        {/* Full screen, on the bar with the title, where a page-level control
            belongs. The bench has its own copy of this button for the phone,
            where the bar is too narrow to spend on it and the bench's own
            corner is where every other bench control already lives; the
            stylesheet shows exactly one of the two. */}
        <button
          className="chemlab-topbar-fullscreen"
          title={fullscreen.isFullscreen ? 'Leave full screen' : 'Fill the screen with the bench'}
          aria-label={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}
          aria-pressed={fullscreen.isFullscreen}
          onClick={fullscreen.toggle}
        >
          <FullscreenIcon exiting={fullscreen.isFullscreen} />
        </button>
      </div>

      <div className="chemlab-shelf-bar">
        {/* The shelf and the picker are the same reagents reached two ways —
            a row of bottles where there is room for one, a searchable grid
            where there is not. Both are rendered and the media query picks;
            a JS width check would have to run before first paint to avoid
            showing the wrong one, and this does not. */}
        <div className="chemlab-tray-wide">
          <SubstanceTray onAdd={handleAddSubstance} disabled={selected.loading} />
        </div>
        <div className="chemlab-tray-narrow">
          <SubstancePicker onAdd={handleAddSubstance} disabled={selected.loading} />
        </div>
      </div>

      {/* `data-mode` so the stylesheet can give the quantitative bench's
          notebook the room its tables need, without touching the free bench. */}
      <div className="chemlab-body" data-mode={quantitative ? 'quantitative' : 'free'}>
        <div className="chemlab-side-panel chemlab-side-panel-left">
          <ControlsPanel
            onPlaceVessel={handlePlaceVessel}
            onPlaceVesselConnected={handlePlaceVesselConnected}
            onPlaceStand={handlePlaceStand}
            hasGlassRod={selected.hasGlassRod}
            glassRodSubstanceId={selected.glassRodSubstanceId}
            onDipGlassRod={handleDipGlassRod}
            onRemoveGlassRod={handleRemoveGlassRod}
            burnerType={selected.burnerType ?? 'none'}
            canHeat={placements[selectedIndex].canHeat}
            onChangeBurner={(burner) => handleChangeBurner(selectedIndex, burner)}
            hasPHPaper={selected.phPaperApplied}
            onToggleTest={handleToggleTest}
            canAddContainer={containers.length < MAX_CONTAINERS}
            hasSubstances={selected.contents.length > 0}
            disabled={selected.loading}
            benchFixed={!!experiment}
            paneOpen={sheet === 'apparatus'}
          />
        </div>

        <div className="chemlab-scene-wrap bench-accent-wash" data-bench-theme={benchTheme ?? undefined}>
          <button
            className="chemlab-clear-bench"
            aria-label={experiment ? 'Start over' : 'Clear bench'}
            title={experiment ? 'Empty the glassware and start the experiment again' : 'Clear the whole bench and start again'}
            onClick={handleClearBench}
          >
            <span aria-hidden="true">↺</span>
            <span className="chemlab-btn-label">{experiment ? 'Start Over' : 'Clear Bench'}</span>
          </button>
          {/* The phone's copy of the control on the title bar above — the two
              are one behaviour, and the stylesheet shows whichever suits the
              width. Where the browser has no element fullscreen the hook pins
              the page instead, so either button always does what it says —
              see useBenchFullscreen. */}
          <button
            className="chemlab-fullscreen-btn"
            title={fullscreen.isFullscreen ? 'Leave full screen' : 'Fill the screen with the bench'}
            aria-label={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}
            aria-pressed={fullscreen.isFullscreen}
            onClick={fullscreen.toggle}
          >
            <FullscreenIcon exiting={fullscreen.isFullscreen} />
          </button>
          {/* The benches' mark and meaning: it shows where a press TAKES the
              bench — a sun on a dark bench, a moon on a light one — as every
              other light/dark switch does. It used to show the bench as it
              was, so the same sun meant the opposite here. */}
          <button
            className="chemlab-bench-btn"
            title={`Make the bench ${benchShowing === 'dark' ? 'light' : 'dark'} — this surface only, not the rest of the page`}
            aria-label={benchShowing === 'dark' ? 'Switch the bench to light' : 'Switch the bench to dark'}
            onClick={toggleBenchTheme}
          >
            <ThemeIcon theme={benchShowing} />
            <span className="chemlab-btn-label">{benchShowing === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          {/* The phone's A / A, beside the bench's lighting rather than on the
              title bar: a phone's bar has room for a way back and a title,
              and these are about how the bench's reagents are laid out. */}
          <div className="chemlab-textsize chemlab-bench-size" role="group" aria-label="Reagent size">
            <button
              title="Fit more reagents on the screen"
              aria-label="Smaller"
              aria-pressed={phoneDensity === 'compact'}
              onClick={() => choosePhoneDensity('compact')}
            >
              A
            </button>
            <button
              title="Room for each reagent's whole name"
              aria-label="Bigger"
              aria-pressed={phoneDensity === 'roomy'}
              onClick={() => choosePhoneDensity('roomy')}
            >
              A
            </button>
          </div>
          <ChemistryLabScene2D
            containers={containers}
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
            onSetTemperature={handleSetTemperature}
            pour={pour}
            swirl={swirl}
            uiScale={uiScale}
            showQuantities={quantitative}
            onStandMenu={(columnBase, x, y) => setStandMenu({ columnBase, x, y })}
            onRequestMenu={(index, x, y) => setContextMenu({ index, x, y })}
          />
          {standMenu && (() => {
            const base = containers[standMenu.columnBase];
            const standType = base.standType;
            const full = standOccupancy(standMenu.columnBase) >= STAND_CAPACITY[standType];
            const benchFull = containers.length >= MAX_CONTAINERS;
            return (
              <StandContextMenu
                x={standMenu.x}
                y={standMenu.y}
                standType={standType}
                // Only glassware this stand can actually hold, and only while
                // it has a free place for it.
                vesselOptions={full || benchFull
                  ? []
                  : VESSEL_TYPES.filter(v => standsFor(v).includes(standType))}
                fullReason={benchFull
                  ? 'The bench is full. Remove a container to make room.'
                  : `This ${STAND_LABELS[standType].toLowerCase()} is full.`}
                onAddContainer={(type) => handleAddToStand(standMenu.columnBase, type)}
                burnerType={base.burnerType}
                canHeat={placements[standMenu.columnBase].canHeat}
                onChangeBurner={(burner) => handleChangeBurner(standMenu.columnBase, burner)}
                onClose={() => setStandMenu(null)}
              />
            );
          })()}

          {/* Asked before the pour, where the hand already is. Alongside the
              bench's other anchored menus so it shares their dismissal and
              their staying-on-screen. */}
          {pendingAdd && findSubstance(pendingAdd.substanceId) && (
            <StrengthChooser
              substance={findSubstance(pendingAdd.substanceId)!}
              x={pendingAdd.x}
              y={pendingAdd.y}
              /* How much this pour is, and how much of it the vessel will hold
                 after — so a strength is chosen for a volume the student can
                 see, not one they find out about in the step log afterwards. */
              pour={perAddFor(findSubstance(pendingAdd.substanceId)!, selected.vesselType)}
              held={selected.contents.find(c => c.substanceId === pendingAdd.substanceId)?.qty ?? 0}
              heldM={selected.contents.find(c => c.substanceId === pendingAdd.substanceId)?.concentrationM
                ?? findSubstance(pendingAdd.substanceId)?.concentrationM}
              vessel={VESSEL_LABELS[selected.vesselType].toLowerCase()}
              onChoose={(molarity) => {
                setPendingAdd(null);
                addSubstance(pendingAdd.substanceId, molarity);
              }}
              onClose={() => setPendingAdd(null)}
            />
          )}

          {contextMenu && (
            <ContainerContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              currentVesselType={containers[contextMenu.index].vesselType}
              onChangeVesselType={(type) => handleChangeVesselType(contextMenu.index, type)}
              burnerType={containers[contextMenu.index].burnerType}
              canHeat={placements[contextMenu.index].canHeat}
              onChangeBurner={(burner) => handleChangeBurner(contextMenu.index, burner)}
              pipeTargets={containers
                .filter((c, i) => i !== contextMenu.index
                  && !pipeLoops(containers[contextMenu.index].id, c.id, containers))
                .map(c => ({ id: c.id, label: containerLabel(c.id) }))}
              pipedToLabel={containers[contextMenu.index].pipeToId
                ? containerLabel(containers[contextMenu.index].pipeToId!)
                : undefined}
              onConnectPipe={(toId) => handleConnectPipe(contextMenu.index, toId)}
              onDisconnectPipe={() => handleDisconnectPipe(contextMenu.index)}
              hasGlassRod={containers[contextMenu.index].hasGlassRod}
              gasTestTargets={containers
                .filter((c, i) => i !== contextMenu.index && !!c.outcome?.gasEvolution)
                .map(c => ({ id: c.id, label: containerLabel(c.id) }))}
              onHoldRodNear={(toId) => handleHoldRodNear(contextMenu.index, toId)}
              canSwirl={containers[contextMenu.index].contents.length > 0}
              onSwirl={() => handleSwirl(contextMenu.index)}
              canClear={containers[contextMenu.index].contents.length > 0}
              onClear={() => handleClearContainer(contextMenu.index)}
              canRemove={containers.length > 1}
              onRemove={() => handleRemoveContainer(contextMenu.index)}
              onClose={() => setContextMenu(null)}
            />
          )}
        </div>

        <div className="chemlab-side-panel chemlab-side-panel-right">
          {experiment ? (
            <GuidedPanel
              experiment={experiment}
              cursor={cursor}
              correction={correction}
              results={guidedResults}
              loading={containers.some(c => c.loading)}
              error={containers.find(c => c.error)?.error}
              onContinue={() => advance()}
              /* The working from the most recent step that HAS one, in the
                 vessel the script is on. A question usually follows the mix it
                 is about, but not always immediately — "now read the pH, then
                 tell me the moles" puts a dip in between, and a dip computes
                 no arithmetic of its own. */
              working={[...selected.steps].reverse().find(st => st.stoichiometry)?.stoichiometry}
              onRestart={restartExperiment}
              onEdit={script && setups.enabled ? editSteps : undefined}
              onLeave={script && setups.enabled ? leaveSetup : undefined}
            />
          ) : (
            <>
              {/* Which bench this is, said where its difference appears. Not in
                  the title, which is centred over a bar with a link either side
                  and has no room to grow. */}
              <p className="chemlab-section-title">
                Steps{quantitative && <span className="chemlab-section-note"> · with amounts</span>}
              </p>
              {(recording || recordingWhy) && (
                <div className="chemlab-recording" role="status">
                  {recording && (
                    <p>
                      <b>Recording</b> · {recording.steps.length} step{recording.steps.length === 1 ? '' : 's'}.
                      {' '}Each pour, pH dip, rod dip, burner, heat, tube and swirl is a step. Press Stop to write them up.
                    </p>
                  )}
                  {recordingWhy && <p className="chemlab-recording-why" role="alert">{recordingWhy}</p>}
                </div>
              )}
              <StepLog
                steps={selected.steps}
                loading={selected.loading}
                substanceCount={selected.contents.length}
                showQuantities={quantitative}
                error={selected.error}
              />
            </>
          )}
        </div>
      </div>

      {/* The two verbs and the notebook, on a phone.
          Adding a reagent is what you came to do, so it stays one tap from the
          bench at every moment rather than a tap to open a sheet plus a tab.
          Hidden above 640px, where all three panels are already on screen. */}
      <div className="chemlab-actionbar">
        <button
          className="chemlab-action chemlab-action-chem"
          aria-pressed={sheet === 'chem'}
          aria-label="Chemicals"
          onClick={() => toggleSheet('chem')}
        >
          <span className="chemlab-action-icon"><ChemicalsIcon /></span>
          <span className="chemlab-action-label">Chemicals</span>
        </button>
        <button
          className="chemlab-action chemlab-action-apparatus"
          aria-pressed={sheet === 'apparatus'}
          aria-label="Apparatus"
          onClick={() => toggleSheet('apparatus')}
        >
          <span className="chemlab-action-icon"><ApparatusIcon /></span>
          <span className="chemlab-action-label">Apparatus</span>
        </button>
        <button
          className="chemlab-action chemlab-action-notes"
          aria-pressed={sheet === 'notes'}
          aria-label={experiment ? 'Steps' : 'Step log'}
          onClick={() => toggleSheet('notes')}
        >
          <span className="chemlab-action-icon"><StepsIcon /></span>
          <span className="chemlab-action-label">
            {experiment ? `Step ${Math.min(cursor + 1, experiment.steps.length)}/${experiment.steps.length}` : 'Steps'}
          </span>
        </button>
      </div>

      {/* The reagent and apparatus panes each need a way out that is not the
          button that opened them — pressing the pressed one to get out is a
          thing you have to know rather than see. The notebook needs none: it
          is what this column shows when nothing else has asked for it, so
          there is nothing for closing it to reveal. */}
      <CloseButton className="chemlab-pane-close" label="Close panel" onClick={closeSheet} />

      {saveDialog && (
        <SaveSetupDialog
          heading={saveDialog === 'copy' ? (script ? 'Save a copy of this experiment' : 'Save as a new setup') : 'Save this bench'}
          initialName={saveDialog === 'copy' && setups.current ? `${setups.current.name} (copy)` : ''}
          initialNotes={saveDialog === 'copy' ? setups.current?.notes : ''}
          chapters={CHEMISTRY_CHAPTER_OPTIONS}
          // The lab spans five chapters and a setup is usually about one: none
          // ticked to start, rather than all.
          initialChapterIds={saveDialog === 'copy' ? (setups.current?.chapterIds ?? []) : []}
          busy={setups.busy}
          error={setups.error}
          onCancel={() => setSaveDialog(null)}
          onSave={(name, notes, chapterIds) => { void saveFromDialog(name, notes, chapterIds); }}
        />
      )}
      {editor && (
        <StepsEditor
          vessels={editor.vessels}
          steps={editor.steps}
          conclusion={editor.conclusion}
          initialName={setups.current?.name ?? ''}
          initialNotes={setups.current?.notes}
          chapters={CHEMISTRY_CHAPTER_OPTIONS}
          initialChapterIds={setups.current?.chapterIds ?? []}
          saveOverName={setups.current?.name}
          busy={setups.busy}
          error={setups.error}
          onSave={(payload, name, notes, chapterIds, asNew) => { void saveExperiment(payload, name, notes, chapterIds, asNew); }}
          onRecordAgain={recordAgain}
          onCancel={cancelEditor}
        />
      )}
    </div>
  );
}
